import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  NodeToolbar,
  useNodeId,
  useReactFlow,
  Handle,
  NodeResizer,
  Position,
} from '@xyflow/react';

// ---------- shared preview cache (module scope) ----------
const PREVIEW_TTL_MS = 10 * 60 * 1000;
const previewCache = new Map(); // key=url -> { data, ts }
const inflight = new Map(); // key=url -> Promise

const normalizeUrl = (u) =>
  !u ? '' : /^https?:\/\//i.test(u) ? u.trim() : `https://${u.trim()}`;
const isLikelyUrl = (u) => /^https?:\/\/[^ ]+\.[^ ]+/.test(u);

function getCachedPreview(nu) {
  if (!nu) return null;
  const key = nu.toLowerCase();
  const entry = previewCache.get(key);
  if (entry && Date.now() - entry.ts < PREVIEW_TTL_MS) return entry.data;
  return null;
}

function setCachedPreview(nu, data) {
  if (!nu) return;
  const key = nu.toLowerCase();
  previewCache.set(key, { data, ts: Date.now() });
}

async function fetchPreviewOnce(nu, signal) {
  const key = nu.toLowerCase();

  // In-flight de-dupe (so multiple clicks don't spam the endpoint)
  if (inflight.has(key)) return inflight.get(key);

  const p = (async () => {
    const res = await fetch(
      `/api/link-preview/?url=${encodeURIComponent(nu)}`,
      { signal }
    );
    if (!res.ok) {
      let msg = 'Preview failed';
      try {
        const j = await res.json();
        msg = j.detail || msg;
      } catch (e) {
        /* noop */
      }
      throw new Error(msg);
    }
    return res.json();
  })().finally(() => inflight.delete(key));

  inflight.set(key, p);
  return p;
}
// ---------------------------------------------------------

const BookmarkNode = ({ data, selected }) => {
  const id = useNodeId();
  const { setNodes } = useReactFlow();

  const [url, setUrl] = useState(data.url ?? '');
  const [preview, setPreview] = useState(data.preview ?? null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // refs used below (define BEFORE use)
  const DEBOUNCE_MS = 250;
  const isEditingRef = useRef(false);
  const typeCommitRef = useRef(null);
  const abortRef = useRef(null);

  // keep local input in sync if parent updates url
  useEffect(() => {
    if (isEditingRef.current) return; // <-- ignore while typing
    if (typeof data.url === 'string' && data.url !== url) {
      setUrl(data.url); // <-- don't force '' unless parent really passed ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.url]);

  // Commit small fields (url); we now also persist preview after fetch
  const commitUrl = useCallback(
    (nu) => {
      if (!id) return;
      setNodes((ns) =>
        ns.map((n) =>
          n.id === id && n.data?.url !== nu
            ? { ...n, data: { ...n.data, url: nu } }
            : n
        )
      );
    },
    [id, setNodes]
  );
  const commitUrlRaw = useCallback(
    (raw) => {
      if (!id) return;
      setNodes((ns) =>
        ns.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, url: raw } } : n
        )
      );
    },
    [id, setNodes]
  );

  const persistPreview = useCallback(
    (nu, previewData) => {
      if (!id) return;
      const now = Date.now();
      setNodes((ns) =>
        ns.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  url: nu || n.data?.url || '',
                  preview: previewData, // <-- persist preview to node.data
                  previewTs: now, // <-- keep a timestamp in case you want TTL logic on mount later
                },
              }
            : n
        )
      );
    },
    [id, setNodes]
  );

  // Persist size only at the end for smoothness
  const handleResizeEnd = useCallback(
    (_evt, params) => {
      if (!id) return;
      const { width, height } = params || {};
      if (width == null && height == null) return;

      setNodes((ns) =>
        ns.map((n) =>
          n.id === id
            ? {
                ...n,
                style: {
                  ...(n.style || {}),
                  ...(width != null ? { width } : {}),
                  ...(height != null ? { height } : {}),
                },
                data: {
                  ...n.data,
                  ...(width != null ? { width } : {}),
                  ...(height != null ? { height } : {}),
                },
              }
            : n
        )
      );
    },
    [id, setNodes]
  );

  const clearPreview = useCallback(
    (nu) => {
      if (!id) return;
      setNodes((ns) =>
        ns.map((n) => {
          if (n.id !== id) return n;
          const { previewTs, ...rest } = n.data || {}; // drop previewTs
          return {
            ...n,
            data: {
              ...rest,
              url: nu || rest.url || '',
              preview: null,
            },
          };
        })
      );
    },
    [id, setNodes]
  );
  // put this helper in the component
  const stopDestructiveKeys = (e) => {
    // Backspace/Delete shouldn't bubble to React Flow
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault(); // don't delete node or navigate back
    }
    e.stopPropagation();

    // also stop native listeners (React Flow listens on document)
    if (e.nativeEvent?.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  const doFetch = useCallback(
    async (raw) => {
      const nu = normalizeUrl((raw || '').trim());
      if (!isLikelyUrl(nu)) {
        setErr('Enter a valid URL');
        setPreview(null);
        return;
      }

      // Fast path: memory cache (TTL)
      const cached = getCachedPreview(nu);
      if (cached) {
        setPreview(cached);
        commitUrl(nu);
        persistPreview(nu, cached); // keep node.data in sync
        return;
      }

      // Abort prior request (if any), then start a new one
      try {
        abortRef.current?.abort();
      } catch (e) {
        /* noop */
      }
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      setLoading(true);
      setErr(null);
      try {
        const data = await fetchPreviewOnce(nu, ctrl.signal);
        setPreview(data);
        setCachedPreview(nu, data);
        commitUrl(nu);
        persistPreview(nu, data); // <-- persist preview to node.data so remounts skip network
      } catch (e) {
        if (e.name === 'AbortError') return;
        setPreview(null);
        setErr(e.message || 'Preview failed');
        clearPreview(nu); // <-- clear parent preview too
      } finally {
        setLoading(false);
      }
    },
    [commitUrl, persistPreview]
  );

  useEffect(() => {
    let timer;
    if (err) {
      timer = setTimeout(() => setErr(null), 5000);
    }
    return () => clearTimeout(timer);
  }, [err]);

  return (
    <div
      className="bookmark-node rounded-lg border"
      style={{
        position: 'relative',
        background: data.bgColor ?? '#fff',
        color: data.fontColor ?? '#111',
        fontSize: data.fontSize ?? 14,
        padding: 10,
        width: data.width ?? 'auto',
        height: data.height ?? 'auto',
      }}
    >
      <NodeToolbar
        isVisible={selected}
        position={Position.Top}
        className="rounded-xl border bg-white p-2 shadow z-50 flex gap-2"
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}

      >
        <input
          type="text"
          placeholder="https://example.com/article"
          value={url}
          onChange={(e) => {
            const val = e.target.value;
            setUrl(val);
            isEditingRef.current = true;

            if (typeCommitRef.current) clearTimeout(typeCommitRef.current);
            typeCommitRef.current = setTimeout(() => {
              commitUrlRaw(val); // push draft to parent while typing
            }, DEBOUNCE_MS);
          }}
          onFocus={() => {
            isEditingRef.current = true;
          }}
          onBlur={(e) => {
            // finalize: stop debouncer, allow parent→child sync, and normalize
            if (typeCommitRef.current) clearTimeout(typeCommitRef.current);
            isEditingRef.current = false;

            const nu = normalizeUrl(e.currentTarget.value.trim());
            if (nu && nu !== (data.url || '')) {
              commitUrl(nu); // your existing normalized commit
            } else {
              // optional: if empty, keep parent's last non-empty (don’t overwrite)
              // commitUrlRaw(''); // only if you want empty propagated too
            }
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              if (typeCommitRef.current) clearTimeout(typeCommitRef.current);
              commitUrlRaw(url); // ensure parent has latest draft
            }
          }}
          className="w-64 border rounded px-2 py-1"
          style={{ background: 'white', color: '#333' }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        />

        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            doFetch(url);
          }}
          className="px-2 py-1 rounded border hover:bg-gray-50 text-gray-400"
          title="Fetch preview"
        >
          Fetch
        </button>
      </NodeToolbar>

      <NodeResizer
        isVisible={selected}
        minWidth={120}
        minHeight={90}
        onResizeEnd={handleResizeEnd}
        onResize={handleResizeEnd}
      />

      {loading && <div className="text-xs text-gray-500">Loading preview…</div>}
      {err && <div className="text-xs text-red-600">{err}</div>}

      {preview && !loading && !err ? (
        <a
          href={preview.url || url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex gap-3 no-underline mt-2"
          style={{ color: 'inherit' }}
        >
          {preview.image ? (
            <img
              src={preview.image}
              alt={preview.title || ''}
              style={{
                width: (data.width || 120) * 0.3, 
                maxHeight: 'auto', // guard if height is undefined
                objectFit: 'cover',
                borderRadius: 8,
                flex: '0 0 auto',
              }}
            />
          ) : null}
          <div className="min-w-0">
            <div className="truncate">
              {preview.title || preview.site || data.label}
            </div>
            {preview.description && (
              <div className="line-clamp-2">{preview.description}</div>
            )}
            <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
              <span className="truncate">
                {preview.site || new URL(normalizeUrl(url)).hostname}
              </span>
            </div>
          </div>
        </a>
      ) : (
        !loading &&
        !err && (
          <a
            href={normalizeUrl(url)}
            target="_blank"
            rel="noopener noreferrer"
            className="grid gap-3 no-underline mt-2"
            style={{ color: 'inherit' }}
          >
            <h3 className="truncate">{data.label}</h3>
            <p className="truncate">{url}</p>
          </a>
        )
      )}

      {/* Handles: same id per side, both source & target */}
      <Handle
        type="source"
        position={Position.Left}
        id="1"
        className="custom-handle-source"
        style={{ left: -6, visibility: selected ? 'visible' : 'hidden' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="1"
        className="custom-handle-target"
        style={{ left: -6, visibility: selected ? 'visible' : 'hidden' }}
      />

      <Handle
        type="source"
        position={Position.Right}
        id="2"
        className="custom-handle-source"
        style={{ right: -6, visibility: selected ? 'visible' : 'hidden' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="2"
        className="custom-handle-target"
        style={{ right: -6, visibility: selected ? 'visible' : 'hidden' }}
      />

      <Handle
        type="source"
        position={Position.Top}
        id="4"
        className="custom-handle-source"
        style={{ top: -6, visibility: selected ? 'visible' : 'hidden' }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="4"
        className="custom-handle-target"
        style={{ top: -6, visibility: selected ? 'visible' : 'hidden' }}
      />

      <Handle
        type="source"
        position={Position.Bottom}
        id="3"
        className="custom-handle-source"
        style={{ bottom: -6, visibility: selected ? 'visible' : 'hidden' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="3"
        className="custom-handle-target"
        style={{ bottom: -6, visibility: selected ? 'visible' : 'hidden' }}
      />
    </div>
  );
};

export default memo(BookmarkNode);
